import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SessionService } from './session.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private session = inject(SessionService);
  private alert = inject(ToastrService);

  login(credentials: { user: string; password: string }) {
    return this.http
      .post<any>(`${environment.apiBaseUrl}/login`, credentials)
      .pipe(
        tap((response) => {
          const data = response.data;

          const role = (
            data.persona?.usuario?.rol?.nombre ?? 'unknown'
          ).toLowerCase();

          const allowed = ['admin'];
          if (!allowed.includes(role)) {
            this.session.clearSession();
            this.alert.error(
              'Usted no tiene permisos para acceder a esta aplicación.',
              'Acceso denegado',
            );
            this.router.navigate(['/login'], {
              queryParams: { error: 'forbidden' },
            });
            return;
          }

          const accessToken = data.accessToken;
          const refreshToken = data.refreshToken;

          const user = data.persona; // o data.persona.usuario si querés guardar solo eso

          this.session.setSession({ accessToken, refreshToken, user, role });
        }),
      );
  }

  logout() {
    const refreshToken = this.session.getRefreshToken();

    // si no hay refresh, igual limpiamos
    if (!refreshToken) {
      this.session.clearSession();
      this.router.navigate(['/login']);
      return;
    }

    this.http
      .post(`${environment.apiBaseUrl}/logout`, { refreshToken })
      .subscribe({
        next: () => {
          this.session.clearSession();
          this.router.navigate(['/login']);
        },
        error: () => {
          // aunque falle, limpiamos igual para no dejar sesión colgada
          this.session.clearSession();
          this.router.navigate(['/login']);
        },
      });
  }

  isAuthenticated(): boolean {
    const access = this.session.getAccessToken();
    const refresh = this.session.getRefreshToken();

    // si no hay ninguno, no hay sesión
    if (!access && !refresh) return false;

    // si access venció PERO hay refresh, seguimos considerándolo logueado
    // el refresh interceptor se encarga de renovar cuando se haga el próximo request
    if (access && this.isTokenExpired(access)) {
      return !!refresh;
    }

    return true;
  }

  getRole(): string | null {
    return this.session.getRole();
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload));
      const now = Math.floor(Date.now() / 1000);
      return decoded.exp < now;
    } catch {
      return true;
    }
  }

  refreshAccessToken() {
    const refreshToken = this.session.getRefreshToken();
    return this.http.post<any>(`${environment.apiBaseUrl}/refresh`, {
      refreshToken,
    });
  }
}
